import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, UserButton } from "@clerk/react";
import { toast } from "sonner";
import { Shield, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { makeApi } from "@/lib/api";
import { dateShort } from "@/lib/format";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CredentialsDialog } from "@/components/drivers/CredentialsDialog";
import { CarrierDialog } from "@/components/owner/CarrierDialog";
import { DeleteCarrierDialog } from "@/components/owner/DeleteCarrierDialog";
import { setViewTenant, clearViewTenant } from "@/lib/viewTenant";
import { ago, StatusPill, CarrierActions, CarrierCard, CarrierFilters } from "@/components/owner/CarrierParts";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-1 text-3xl font-bold text-white tabular-nums" style={{ fontFamily: "Outfit" }}>{value}</div>
  </div>
);

export default function OwnerConsole() {
  const { getToken } = useAuth();
  const nav = useNavigate();
  useEffect(() => { clearViewTenant(); }, []);
  const viewData = (r) => { setViewTenant(r); nav("/admin"); };
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [rows, setRows] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [creds, setCreds] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const on = { view: viewData, admin: setDialog, toggle: setToggling, del: setDeleting };
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const refresh = () => api.get("/owner/carriers").then(({ data }) => setRows(data)).catch(() => { setRows([]); toast.error("Could not load carriers"); });
  useEffect(() => { refresh(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async () => {
    const t = toggling;
    try {
      await api.post(`/owner/carriers/${t.id}/${t.status === "active" ? "deactivate" : "reactivate"}`);
      toast.success(`${t.name} ${t.status === "active" ? "deactivated" : "reactivated"}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change status"); }
    setToggling(null);
  };
  const all = rows || [];
  const needle = q.trim().toLowerCase();
  const list = all.filter((r) => (statusF === "all" || (statusF === "active") === (r.status === "active")) &&
    (!needle || r.name.toLowerCase().includes(needle) || r.admins.some((a) => `${a.email} ${a.name || ""}`.toLowerCase().includes(needle))));
  const sum = (k) => all.reduce((s, r) => s + (r[k] || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="owner-console">
      <header className="h-16 border-b border-slate-800 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-amber-400 text-slate-950 grid place-items-center"><Shield className="w-5 h-5" /></div>
          <div><div className="font-bold leading-tight" style={{ fontFamily: "Outfit" }}>Owner console</div><div className="text-[11px] text-slate-400">Platform administration</div></div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1 whitespace-nowrap" data-testid="owner-open-workspace"><span className="hidden sm:inline">My carrier workspace</span><span className="sm:hidden">Workspace</span> <ArrowRight className="w-4 h-4" /></Link>
          <UserButton afterSignOutUrl="/" />
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto p-4 md:p-8 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>Carriers</h1>
            <p className="text-sm text-slate-400 mt-1">Each carrier is a separate workspace. Carrier admins never see this console.</p>
          </div>
          <Button onClick={() => setDialog({})} className="bg-amber-400 text-slate-950 hover:bg-amber-300 rounded-full" data-testid="owner-new-carrier-btn"><Plus className="w-4 h-4 mr-1" /> New carrier</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Carriers" value={all.length} testId="owner-stat-carriers" />
          <Stat label="Active" value={all.filter((r) => r.status === "active").length} testId="owner-stat-active" />
          <Stat label="Loads" value={sum("loads")} testId="owner-stat-loads" />
          <Stat label="Drivers" value={sum("drivers")} testId="owner-stat-drivers" />
        </div>
        <CarrierFilters q={q} setQ={setQ} statusF={statusF} setStatusF={setStatusF} shown={list.length} total={all.length} />
        {rows !== null && all.length > 0 && !list.length && <div className="rounded-2xl border border-slate-800 py-10 text-center text-slate-500" data-testid="owner-no-match">No carriers match your search.</div>}
        <div className="md:hidden space-y-3" data-testid="owner-carrier-cards">
          {rows === null && <div className="py-10 text-center text-slate-500">Loading…</div>}
          {list.map((r) => <CarrierCard key={r.id} r={r} on={on} />)}
        </div>
        <div className={`hidden ${rows !== null && all.length > 0 && !list.length ? "" : "md:block"} rounded-2xl border border-slate-800 overflow-x-auto`}>
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wider">
              <tr>{["Carrier", "", "Status", "Admins", "Loads", "Drivers", "Invoices", "Last active", "Created"].map((h, i) => <th key={h || i} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
              {list.map((r) => (
                <tr key={r.id} className="border-t border-slate-800" data-testid={`carrier-row-${r.id}`}>
                  <td className="px-4 py-3 font-semibold text-white">{r.name}{r.is_mine && <span className="ml-2 text-[10px] uppercase text-amber-300">yours</span>}</td>
                  <td className="px-2 py-2 whitespace-nowrap"><CarrierActions r={r} on={on} /></td>
                  <td className="px-4 py-3"><StatusPill r={r} testId={`carrier-status-${r.id}`} /></td>
                  <td className="px-4 py-3 text-slate-300">{r.admins.map((a) => <div key={a.user_id} className="leading-tight py-0.5">{a.name || a.email}<div className="text-xs text-slate-500">{a.email}</div></div>)}</td>
                  <td className="px-4 py-3 tabular-nums">{r.loads}</td>
                  <td className="px-4 py-3 tabular-nums">{r.drivers}</td>
                  <td className="px-4 py-3 tabular-nums">{r.invoices}</td>
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap" data-testid={`carrier-last-active-${r.id}`}>{ago(r.last_active_at)}</td>
                  <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{dateShort(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <CarrierDialog carrier={dialog} api={api} onClose={() => setDialog(null)} onDone={(c) => { setDialog(null); setCreds(c); refresh(); }} />
      <DeleteCarrierDialog carrier={deleting} api={api} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); refresh(); }} />
      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} who="carrier admin" />
      <ConfirmDialog open={!!toggling} title={toggling?.status === "active" ? `Deactivate ${toggling?.name}?` : `Reactivate ${toggling?.name}?`}
        description={toggling?.status === "active" ? "Its admins and drivers are blocked from the app right away. Their data is kept." : "Its admins and drivers can sign in again."}
        confirmLabel={toggling?.status === "active" ? "Deactivate" : "Reactivate"} onConfirm={toggle} onCancel={() => setToggling(null)} testId="carrier-toggle-confirm" />
    </div>
  );
}
